import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Phone,
  Mail,
  Facebook,
  Twitter,
  Youtube,
  Linkedin,
  Instagram,
  ShieldCheck,
  CreditCard,
  Smartphone,
  Wallet
} from 'lucide-react';
import logo from '../../assets/images/lmsLogo.png';

export default function Footer() {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [hoveredSocial, setHoveredSocial] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const footerData = {
    information: {
      title: 'Information',
      links: [
        { name: 'About Us', path: '/about-us' },
        { name: 'FAQs', path: '/faq' },
        { name: 'Contact Us', path: '/contact-us' },
        { name: 'Privacy Policy', path: '/privacy-policy' },
        { name: 'Terms & Conditions', path: '/terms-and-conditions' },
        { name: 'Refund Policy', path: '/refund-policy' },
      ]
    },
    quickLinks: {
      title: 'Quick Links',
      links: [
        { name: 'All Courses', path: '/allCourses/all' },
        { name: 'Industry Partners', path: '/partner-course' },
        { name: 'Help & Support', path: '/contact-us' },
      ]
    }
  };

  const socialLinks = [
    { icon: Facebook, url: 'https://www.facebook.com/p/SkillServe-61575759530831/', name: 'Facebook' },
    { icon: Youtube, url: 'https://www.youtube.com/channel/UCnifnE0swW4vqk6EbDJWvvg', name: 'YouTube' },
    { icon: Linkedin, url: 'https://www.linkedin.com/company/skillserve-in/', name: 'LinkedIn' },
    { icon: Instagram, url: 'https://www.instagram.com/skill.serve/', name: 'Instagram' },
    { icon: Twitter, url: 'https://x.com/serve_skill', name: 'Twitter' },
  ];

  const wrapStyle = {
    width: '100%',
    background: '#f8fafb',
    color: '#334155',
    position: 'relative',
    overflow: 'hidden',
  };

  const topAccentStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, transparent 0%, #fdd649 20%, #fdd649 80%, transparent 100%)',
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: isDesktop ? '100px 24px 50px' : isMobile ? '70px 20px 40px' : '80px 24px 45px',
  };

  const mainGridStyle = {
    display: 'grid',
    gridTemplateColumns: isDesktop ? '1.8fr 1fr 1fr 1.2fr' : isMobile ? '1fr' : 'repeat(2, 1fr)',
    gap: isDesktop ? '80px' : '50px',
    marginBottom: '80px',
  };

  const logoSectionStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  };

  const logoStyle = {
    width: isDesktop ? '200px' : '170px',
    height: 'auto',
  };

  const descriptionStyle = {
    fontSize: '1rem',
    lineHeight: 1.9,
    color: '#64748b',
    fontWeight: 600,
    maxWidth: '380px',
  };

  const sectionTitleStyle = {
    fontSize: '1.1rem',
    fontWeight: 700,
    marginBottom: '24px',
    color: '#0b3161',
  };

  const linkStyle = (isHovered) => ({
    display: 'block',
    color: isHovered ? '#fdd649' : '#64748b',
    textDecoration: 'none',
    fontSize: '0.95rem',
    fontWeight: 500,
    marginBottom: '16px',
    transition: 'color 0.3s ease',
    cursor: 'pointer',
  });

  const contactItemStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '20px',
    color: '#64748b',
    fontSize: '0.95rem',
    fontWeight: 500,
  };

  const iconWrapperStyle = {
    flexShrink: 0,
    marginTop: '2px',
  };

  const socialContainerStyle = {
    display: 'flex',
    gap: '12px',
    marginTop: '30px',
    flexWrap: 'wrap',
  };

  const socialButtonStyle = (isHovered) => ({
    width: '42px',
    height: '42px',
    borderRadius: '8px',
    background: isHovered ? '#fdd649' : '#ffffff',
    border: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    color: isHovered ? '#0b3161' : '#64748b',
    boxShadow: isHovered ? '0 4px 12px rgba(253, 214, 73, 0.3)' : 'none',
  });

  const dividerStyle = {
    height: '1px',
    background: '#e2e8f0',
    marginBottom: '40px',
  };

  const bottomBarStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '24px',
  };

  const copyrightStyle = {
    fontSize: '0.9rem',
    color: '#94a3b8',
    fontWeight: 500,
  };

  const paymentContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  };

  const paymentTextStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#64748b',
  };

  const paymentBadgeStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: '#ffffff',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#64748b',
    border: '1px solid #e2e8f0',
  };

  return (
    <footer style={wrapStyle}>
      <div style={topAccentStyle} />

      <div style={containerStyle}>
        {/* Main Footer Grid */}
        <div style={mainGridStyle}>
          {/* Logo & Description */}
          <div style={logoSectionStyle}>
            <img
              src={logo}
              alt="SkillServe Logo"
              style={logoStyle}
            />

            <p style={descriptionStyle}>
              Skill Serve is your gateway to practical, industry-relevant learning.
              Offering flexible online courses designed by experts.
            </p>
          </div>

          {/* Information Links */}
          <div>
            <h3 style={sectionTitleStyle}>
              {footerData.information.title}
            </h3>
            {footerData.information.links.map((link, index) => {
              const isHovered = hoveredLink === `info-${index}`;
              return (
                <a
                  key={index}
                  href={link.path}
                  style={linkStyle(isHovered)}
                  onMouseEnter={() => setHoveredLink(`info-${index}`)}
                  onMouseLeave={() => setHoveredLink(null)}
                >
                  {link.name}
                </a>
              );
            })}
          </div>

          {/* Quick Links */}
          <div>
            <h3 style={sectionTitleStyle}>
              {footerData.quickLinks.title}
            </h3>
            {footerData.quickLinks.links.map((link, index) => {
              const isHovered = hoveredLink === `quick-${index}`;
              return (
                <a
                  key={index}
                  href={link.path}
                  style={linkStyle(isHovered)}
                  onMouseEnter={() => setHoveredLink(`quick-${index}`)}
                  onMouseLeave={() => setHoveredLink(null)}
                >
                  {link.name}
                </a>
              );
            })}
          </div>

          {/* Contact & Social */}
          <div>
            <h3 style={sectionTitleStyle}>
              Contact Us
            </h3>

            <div style={contactItemStyle}>
              <div style={iconWrapperStyle}>
                <MapPin size={18} color="#fdd649" />
              </div>
              <span>Sector 33, Gurugram - 122001</span>
            </div>

            <div style={contactItemStyle}>
              <div style={iconWrapperStyle}>
                <Phone size={18} color="#fdd649" />
              </div>
              <span>+91-9484794843</span>
            </div>

            <div style={contactItemStyle}>
              <div style={iconWrapperStyle}>
                <Mail size={18} color="#fdd649" />
              </div>
              <span>support@skillserve.in</span>
            </div>

            {/* Social Links */}
            <div style={socialContainerStyle}>
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                const isHovered = hoveredSocial === index;

                return (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <div
                      style={socialButtonStyle(isHovered)}
                      onMouseEnter={() => setHoveredSocial(index)}
                      onMouseLeave={() => setHoveredSocial(null)}
                    >
                      <Icon size={18} />
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={dividerStyle} />

        {/* Bottom Bar */}
        <div style={bottomBarStyle}>
          <p style={copyrightStyle}>
            Copyright © 2024 SkillServe. All rights reserved.
          </p>

          <div style={paymentContainerStyle}>
            <div style={paymentTextStyle}>
              <ShieldCheck size={16} color="#64748b" />
              Secure Payments
            </div>

            <div style={paymentBadgeStyle}>
              <CreditCard size={14} />
              Cards
            </div>

            <div style={paymentBadgeStyle}>
              <Smartphone size={14} />
              UPI
            </div>

            <div style={paymentBadgeStyle}>
              <Wallet size={14} />
              Wallets
            </div>

            <div style={paymentBadgeStyle}>
              <img
                src="https://cdn.razorpay.com/logo.svg"
                alt="Razorpay"
                style={{ height: '14px' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
